import React, { createContext, useContext, useCallback, useEffect, useRef } from 'react';
import { useSignalR } from '../hooks/useSignalR';
import { useSiteConfig } from './SiteConfigContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { mapNextDrawForFrontend, mergeSignalRDrawUpdate, unwrapOperationData } from '../utils/drawsApi';
import { DRAW_LIFECYCLE_STATES, normalizeDrawStatus } from '../utils/drawLifecycle';

const SignalRContext = createContext(undefined);

// Routes where a 'Started' draw event should yank the user to the home page.
// We do NOT redirect from /account, /checkout, /receipt, etc. — that's hostile.
const REDIRECT_ON_STARTED = [
  '/get-tickets',
  '/purchase',
  '/square-game',
  '/pick-your-square',
  '/chase-the-ace',
  '/superball-bingo',
  '/elimination',
  '/prize-draw',
];

function shouldRedirectOnStarted(pathname) {
  if (!pathname) return false;
  return REDIRECT_ON_STARTED.some((p) => pathname.startsWith(p));
}

export function SignalRProvider({ children }) {
  const { nextDraw, setNextDraw, refetch } = useSiteConfig();
  const navigate = useNavigate();
  const location = useLocation();

  // Pub/sub channels — let consumers subscribe without coupling to SignalR.
  const drawStatusSubscribersRef = useRef(new Set());
  const additionalDrawStatusSubscribersRef = useRef(new Set());
  const jackpotTickSubscribersRef = useRef(new Set());
  const drawUpdateSubscribersRef = useRef(new Set());

  // Track last known jackpot so we can emit deltas reliably even if the
  // currently-rendered layout has a stale `nextDraw` reference.
  const lastJackpotRef = useRef(null);
  const delayedRefetchTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (delayedRefetchTimerRef.current) {
        clearTimeout(delayedRefetchTimerRef.current);
      }
    };
  }, []);

  const scheduleDelayedRefetch = useCallback((delayMs = 2000) => {
    if (delayedRefetchTimerRef.current) {
      clearTimeout(delayedRefetchTimerRef.current);
    }
    delayedRefetchTimerRef.current = setTimeout(() => {
      delayedRefetchTimerRef.current = null;
      refetch();
    }, delayMs);
  }, [refetch]);

  const subscribeDrawStatus = useCallback((callback) => {
    drawStatusSubscribersRef.current.add(callback);
    return () => drawStatusSubscribersRef.current.delete(callback);
  }, []);

  const subscribeAdditionalDrawStatus = useCallback((callback) => {
    additionalDrawStatusSubscribersRef.current.add(callback);
    return () => additionalDrawStatusSubscribersRef.current.delete(callback);
  }, []);

  // Jackpot tick: emits { prev, next, delta, drawId } whenever a userPurchase
  // event reports a jackpot increase. Animation components listen here so they
  // don't need to rely on prop-diffing a possibly-stale `draw` reference.
  const subscribeJackpotTick = useCallback((callback) => {
    jackpotTickSubscribersRef.current.add(callback);
    return () => jackpotTickSubscribersRef.current.delete(callback);
  }, []);

  const subscribeDrawUpdate = useCallback((callback) => {
    drawUpdateSubscribersRef.current.add(callback);
    return () => drawUpdateSubscribersRef.current.delete(callback);
  }, []);

  const emitJackpotTick = useCallback((tick) => {
    jackpotTickSubscribersRef.current.forEach((cb) => {
      try { cb(tick); } catch (_) { /* swallow */ }
    });
  }, []);

  const handleUserPurchase = useCallback((nextDrawData) => {
    if (!nextDrawData) return;

    const rawEnvelope = unwrapOperationData(nextDrawData);
    if (!rawEnvelope || typeof rawEnvelope !== 'object') return;

    // API userPurchase messages are a purchase envelope:
    // { amount, drawId, organizationId, memberId, nextDraw }. The jackpot
    // fields live under nextDraw, so unwrap that before mapping draw state.
    const rawData = rawEnvelope.nextDraw || rawEnvelope.NextDraw || rawEnvelope;
    if (!rawData || typeof rawData !== 'object') return;

    const drawData = {
      ...rawData,
      id: rawData.id || rawData.Id || rawEnvelope.drawId || rawEnvelope.DrawId,
      drawId: rawData.drawId || rawData.DrawId || rawEnvelope.drawId || rawEnvelope.DrawId,
      organizationId: rawData.organizationId || rawData.OrganizationId || rawEnvelope.organizationId || rawEnvelope.OrganizationId,
    };

    const normalizedData = mapNextDrawForFrontend(drawData) || {
      id: drawData.id || drawData.Id,
      drawDate: drawData.drawDate || drawData.DrawDate,
      jackpot: drawData.jackpot || drawData.Jackpot,
      nextDraw: drawData.nextDraw || drawData.NextDraw,
      ...drawData,
    };

    // Detect jackpot delta and emit central tick so all consumers animate
    // off the same event rather than fragile prop-diffs.
    const nextJackpotRaw =
      normalizedData.current_jackpot ??
      normalizedData.currentJackpot ??
      normalizedData.jackpot ??
      normalizedData.CurrentJackpot ??
      null;
    const nextJackpot = typeof nextJackpotRaw === 'string' ? parseFloat(nextJackpotRaw) : nextJackpotRaw;
    const prevJackpot = lastJackpotRef.current;

    if (
      typeof nextJackpot === 'number' &&
      !Number.isNaN(nextJackpot) &&
      typeof prevJackpot === 'number' &&
      nextJackpot > prevJackpot
    ) {
      emitJackpotTick({
        prev: prevJackpot,
        next: nextJackpot,
        delta: nextJackpot - prevJackpot,
        drawId: normalizedData.id,
      });
    }
    if (typeof nextJackpot === 'number' && !Number.isNaN(nextJackpot)) {
      lastJackpotRef.current = nextJackpot;
    }

    // Non-destructive optimistic merge — preserve rich draw metadata
    // (status, game_type, opening_date, utc_offset, ticket_price, goal_*, etc.)
    // while updating jackpot and prize amounts.
    setNextDraw((prev) => mergeSignalRDrawUpdate(prev, drawData));
    drawUpdateSubscribersRef.current.forEach((cb) => {
      try { cb(normalizedData); } catch (_) { /* swallow */ }
    });
  }, [setNextDraw, emitJackpotTick]);

  const handleDrawStatus = useCallback((status) => {
    const normalizedStatus = normalizeDrawStatus(status);
    const statusStr = typeof status === 'string'
      ? status
      : (status?.status || status?.Status || status?.drawStatus || status?.DrawStatus || normalizedStatus || '');

    // Broadcast raw event to subscribers (e.g. useDrawStatus state machine).
    drawStatusSubscribersRef.current.forEach((cb) => {
      try { cb(status); } catch (_) { /* swallow */ }
    });

    if (
      normalizedStatus === DRAW_LIFECYCLE_STATES.WAITING ||
      normalizedStatus === DRAW_LIFECYCLE_STATES.STARTED ||
      normalizedStatus === DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING
    ) {
      // Immediately reflect the blocking status on nextDraw so isDrawClosed
      // and all layouts/purchase pages lock ticket purchasing right away.
      if (statusStr) {
        setNextDraw((prev) => (prev ? { ...prev, status: statusStr } : prev));
      }
    }

    if (normalizedStatus === DRAW_LIFECYCLE_STATES.STARTED || normalizedStatus === DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING) {
      // Only yank from purchase pages — never from /account, /checkout, etc.
      if (shouldRedirectOnStarted(location.pathname)) {
        navigate('/');
      }
      if (normalizedStatus === DRAW_LIFECYCLE_STATES.MANUAL_ENVELOPE_PENDING) {
        refetch();
        scheduleDelayedRefetch(2000);
      }
      return;
    }

    if (normalizedStatus === DRAW_LIFECYCLE_STATES.FINISHED || normalizedStatus === DRAW_LIFECYCLE_STATES.REOPENED) {
      // Draw cycle advanced — refetch immediately and schedule a follow-up refetch
      // in case the backend broadcasted "Drawn"/"Finished" just before flushing Redis.
      refetch();
      scheduleDelayedRefetch(2000);
    }
  }, [navigate, location, refetch, setNextDraw, scheduleDelayedRefetch]);

  const handleAdditionalDrawStatus = useCallback((additionalDrawData) => {
    additionalDrawStatusSubscribersRef.current.forEach((cb) => {
      try { cb(additionalDrawData); } catch (_) { /* swallow */ }
    });
    refetch();
    scheduleDelayedRefetch(2000);
  }, [refetch, scheduleDelayedRefetch]);

  // userRegister is currently informational only on the player FE.
  const handleUserRegister = useCallback(() => {}, []);

  const { connectionStatus, isConnected } = useSignalR({
    onUserPurchase: handleUserPurchase,
    onDrawStatus: handleDrawStatus,
    onAdditionalDrawStatus: handleAdditionalDrawStatus,
    onUserRegister: handleUserRegister,
  });

  // Seed lastJackpotRef from the current site nextDraw so the very first
  // SignalR event after page load has a baseline to compare against.
  if (lastJackpotRef.current == null && nextDraw) {
    const seedRaw = nextDraw.current_jackpot ?? nextDraw.currentJackpot ?? nextDraw.jackpot ?? null;
    const seed = typeof seedRaw === 'string' ? parseFloat(seedRaw) : seedRaw;
    if (typeof seed === 'number' && !Number.isNaN(seed)) {
      lastJackpotRef.current = seed;
    }
  }

  const contextValue = {
    connectionStatus,
    isConnected,
    subscribeDrawStatus,
    subscribeAdditionalDrawStatus,
    subscribeJackpotTick,
    subscribeDrawUpdate,
  };

  return (
    <SignalRContext.Provider value={contextValue}>
      {children}
    </SignalRContext.Provider>
  );
}

export function useSignalRContext() {
  const context = useContext(SignalRContext);
  if (context === undefined) {
    throw new Error('useSignalRContext must be used within a SignalRProvider');
  }
  return context;
}

export function useOptionalSignalRContext() {
  return useContext(SignalRContext) || null;
}
